import { type Result, err, ok } from 'neverthrow';
import type { ZodType } from 'zod';

import { ParserError } from './error';
import type { ZodDescriptor } from './types/special';
import {
  isJsonPrimitive,
  isZodAny,
  isZodArray,
  isZodBoolean,
  isZodEnum,
  isZodLiteral,
  isZodNumber,
  isZodString,
} from './zod-guards';

export const serializeZod = (
  zodSchema: ZodType,
): Result<ZodDescriptor, ParserError> => {
  if (isZodString(zodSchema)) {
    return ok({ zod: 'string' });
  }

  if (isZodNumber(zodSchema)) {
    return ok({ zod: 'number' });
  }

  if (isZodBoolean(zodSchema)) {
    return ok({ zod: 'boolean' });
  }

  if (isZodAny(zodSchema)) {
    return ok({ zod: 'any' });
  }

  if (isZodEnum(zodSchema)) {
    return ok({
      zod: 'enum',
      values: [...zodSchema.options],
    });
  }

  if (isZodLiteral(zodSchema)) {
    const { value } = zodSchema._def;

    if (!isJsonPrimitive(value)) {
      return err(ParserError.ZodSerializationError);
    }

    return ok({
      zod: 'literal',
      value,
    });
  }

  if (isZodArray(zodSchema)) {
    return serializeZod(zodSchema._def.type).map((items) => ({
      zod: 'array' as const,
      items,
    }));
  }

  return err(ParserError.ZodSerializationError);
};
